import {useMutation} from '@tanstack/react-query';
import {useCallback, useEffect} from 'react';
import {useLocale} from 'state/locale';
import {UiLongTask} from 'types/long-task';
import {isUiLongTask} from 'utils/long-task';
import {toCamelCase} from 'utils/object';
import {request} from 'utils/request-axios';

import {UseUILongTaskReturnType} from './useLongTask';

const usePollLongTaskService = () => {
    const locale = useLocale();

    return useMutation<UiLongTask, Error, UiLongTask>({
        mutationFn: (longTask) =>
            request({locale, method: 'GET', url: longTask.url}).then(
                (response) => toCamelCase(response),
            ),
    });
};

const POLL_INTERVAL = 2000;
/**
 * Responsibility:
 * - If LongTask is defined and not finished, poll the long task every 2 seconds
 */
const usePollLongTask = (methods: UseUILongTaskReturnType) => {
    const pollLongTaskApi = usePollLongTaskService();

    const shouldPoll = useCallback(() => {
        if (!methods.longTask) return false;
        else if (methods.longTask.finished) return false;
        else if (pollLongTaskApi.isPending) return false;
        else return true;
    }, [methods, pollLongTaskApi]);

    useEffect(() => {
        const handlePollLongTask = async () => {
            if (!shouldPoll()) return;
            if (!methods.longTask) throw new Error('No long task to poll');
            await pollLongTaskApi
                .mutateAsync(methods.longTask)
                .then((response) => {
                    if (isUiLongTask(response)) {
                        methods.setLongTask(response);
                    } else {
                        methods.setLongTask(undefined);
                        throw new Error(
                            'UI Long Task response was not expected shape',
                        );
                    }
                })
                .catch((error) => {
                    throw new Error(error);
                });
        };

        const interval = setInterval(async () => {
            await handlePollLongTask();
        }, POLL_INTERVAL);

        return () => {
            clearInterval(interval);
        };
    }, [methods, pollLongTaskApi]);
};

export default usePollLongTask;
